/**
 * TreatmentRequestChangeCard - Rich card for a single treatment record request's changes.
 *
 * Mirrors ProviderChangeCard patterns:
 * - Header with request name, record types, status badge, channel badge
 * - Key fields grid, channel section
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes + "Mark as Transferred" button
 *
 * Designed to eventually nest under providers, so kept compact.
 */

import {
  FileText,
  Mail,
  MapPin,
  Phone,
  Printer,
} from 'lucide-react';
import * as React from 'react';

import type { BadgeColor } from '@/components/ui/catalyst/badge';
import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ChangedTreatmentRequestEntity,
  TreatmentRecordRequestChannelType,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/crossMatterChangesService';

import type { CheckableFieldConfig } from '../utils/buildCheckableItems';
import { buildCheckableItems } from '../utils/buildCheckableItems';
import { formatEnumValue } from '../utils/formatting';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import type { FieldConfig } from './KeyFieldsGrid';
import { KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';

// ============================================================================
// Treatment-request-specific config
// ============================================================================

const REQUEST_STATUS_COLORS: Record<TreatmentRecordRequestStatus, BadgeColor> =
  {
    not_ready: 'zinc',
    pending: 'yellow',
    request_submitted: 'blue',
    request_received: 'sky',
    awaiting_invoice: 'orange',
    pending_payment: 'amber',
    records_pending: 'violet',
    records_delivered: 'green',
    records_ingested: 'emerald',
    rejected: 'red',
    cancelled: 'rose',
    auto_retried: 'zinc',
  };

const REQUEST_STATUS_LABELS: Record<TreatmentRecordRequestStatus, string> = {
  not_ready: 'Not Ready',
  pending: 'Ready',
  request_submitted: 'Sent',
  request_received: 'Confirmed',
  awaiting_invoice: 'Invoice Pending',
  pending_payment: 'Invoice Ready',
  records_pending: 'Records Pending',
  records_delivered: 'Delivered',
  records_ingested: 'Processed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  auto_retried: 'Auto-Retried',
};

const CHANNEL_ICONS: Record<
  TreatmentRecordRequestChannelType,
  typeof Phone
> = {
  phone: Phone,
  fax: Printer,
  email: Mail,
  mail: MapPin,
  portal: FileText,
  human: Phone,
};

const KEY_FIELDS: FieldConfig[] = [
  { key: 'actor', label: 'Initiated By' },
  { key: 'is_certified', label: 'Certified' },
  { key: 'is_urgent', label: 'Urgent' },
];

const SECONDARY_FIELDS: FieldConfig[] = [
  { key: 'provider_id', label: 'Provider ID' },
  { key: 'task_id', label: 'Task ID' },
];

const TR_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'status', type: 'field' },
  { key: 'provided_records', type: 'nested' },
  { key: 'actor', type: 'field' },
  { key: 'is_certified', type: 'field' },
  { key: 'is_urgent', type: 'field' },
  { key: 'channel', type: 'nested' },
  { key: 'fax_transmission_status', type: 'nested' },
  { key: 'email_transmission_status', type: 'nested' },
  { key: 'metadata', type: 'nested' },
  { key: 'provider_id', type: 'field' },
  { key: 'task_id', type: 'field' },
];

// ============================================================================
// Main Component
// ============================================================================

export const TreatmentRequestChangeCard: React.FC<{
  entity: ChangedTreatmentRequestEntity;
  /** When true, hides TransferButton and reports readiness via callback */
  nested?: boolean;
  /** Called when nested card's transfer readiness changes */
  onTransferReady?: (entityId: string, ready: boolean) => void;
}> = ({ entity, nested, onTransferReady }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  // Compute checkable items for the transfer checklist
  const checkableItems = React.useMemo<string[]>(
    () =>
      buildCheckableItems(
        entity.change_type,
        entity.current ?? entity.old,
        entity.events,
        changes,
        TR_CHECKABLE_FIELDS,
      ),
    [entity, changes],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);
  const transferEventIds = React.useMemo(() => entity.events.map((e) => e.id), [entity.events]);
  const transfer = useTransferAction(transferEventIds);

  // Report readiness to parent when nested
  React.useEffect(() => {
    if (nested && onTransferReady) {
      onTransferReady(entity.entity_id, checklist.buttonEnabled);
    }
  }, [nested, onTransferReady, entity.entity_id, checklist.buttonEnabled]);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Provided records for name computation
  const providedRecords = (displayData.provided_records ?? []) as string[];

  // Display name/description logic
  const displayName =
    providedRecords.length > 0
      ? `Request for ${providedRecords.join(' + ')}`
      : entity.name;
  const displayDescription =
    providedRecords.length > 0 ? entity.name : entity.description;

  // Status
  const status = displayData.status as
    | TreatmentRecordRequestStatus
    | undefined;
  const statusChanged = changes.isFieldChanged('status');

  // Provided records change
  const recordsChanged =
    changes.isFieldChanged('provided_records') ||
    changes.hasChangesUnder('provided_records');

  // Channel
  const channel = displayData.channel as
    | Record<string, unknown>
    | undefined;
  const channelChanged =
    changes.hasChangesUnder('channel') || changes.isFieldChanged('channel');
  const channelType = channel?.type as
    | TreatmentRecordRequestChannelType
    | undefined;

  // Secondary fields
  const visibleSecondaryFields = SECONDARY_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedSecondaryFields = visibleSecondaryFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedSecondaryFields = visibleSecondaryFields.filter(
    (f) => !changes.isFieldChanged(f.key),
  );

  // Transmission statuses
  const faxStatus = displayData.fax_transmission_status as
    | Record<string, unknown>
    | undefined;
  const faxStatusChanged =
    changes.hasChangesUnder('fax_transmission_status') ||
    changes.isFieldChanged('fax_transmission_status');

  const emailStatus = displayData.email_transmission_status as
    | Record<string, unknown>
    | undefined;
  const emailStatusChanged =
    changes.hasChangesUnder('email_transmission_status') ||
    changes.isFieldChanged('email_transmission_status');

  const transmissions = [
    faxStatus && { status: faxStatus, changed: faxStatusChanged, type: 'fax' },
    emailStatus && {
      status: emailStatus,
      changed: emailStatusChanged,
      type: 'email',
    },
  ].filter(Boolean) as {
    status: Record<string, unknown>;
    changed: boolean;
    type: string;
  }[];
  const transmissionsChanged = transmissions.some((t) => t.changed);

  // Metadata changes
  const metadataChanged =
    changes.hasChangesUnder('metadata') ||
    changes.isFieldChanged('metadata');

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    KEY_FIELDS.some(
      (f) =>
        data[f.key] != null ||
        data[f.key] !== '' ||
        changes.isFieldChanged(f.key),
    ) ||
    statusChanged ||
    recordsChanged ||
    channelChanged ||
    (channel != null) ||
    faxStatusChanged ||
    emailStatusChanged ||
    metadataChanged ||
    visibleSecondaryFields.length > 0 ||
    isUpdatedWithoutDiffs;

  // Compute unchanged content for ShowMoreSection
  const hasUnchangedContent =
    (entity.change_type === 'UPDATED' || entity.change_type === 'SAME') &&
    (KEY_FIELDS.some(
      (f) =>
        (data[f.key] != null && data[f.key] !== '') &&
        !changes.isFieldChanged(f.key),
    ) ||
      (channel && !channelChanged) ||
      (!transmissionsChanged && transmissions.length > 0) ||
      unchangedSecondaryFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={displayName}
      description={displayDescription}
      icon={FileText}
      iconBgClassName="bg-indigo-50"
      iconTextClassName="text-indigo-600"
      badges={
        channelType && (
          <Badge color="zinc" className="text-[10px] shrink-0">
            {formatEnumValue(channelType)}
          </Badge>
        )
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events}
          checklist={checklist}
        />
      )}

      {/* Status (when changed, show prominently) */}
      {statusChanged && status && (
        <ChangedFieldHighlight
          label="Status"
          fieldKey="status"
          changes={changes}
          checklist={checklist}
        >
          <Badge
            color={REQUEST_STATUS_COLORS[status] ?? 'zinc'}
            className="text-xs"
          >
            {REQUEST_STATUS_LABELS[status] ?? formatEnumValue(status)}
          </Badge>
        </ChangedFieldHighlight>
      )}

      {/* Provided records (when changed) */}
      {recordsChanged && (
        <ChangedFieldHighlight
          label="Provided Records"
          fieldKey="provided_records"
          changes={changes}
          checklist={checklist}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {providedRecords.length > 0 ? (
              providedRecords.map((rec) => (
                <Badge key={rec} color="zinc" className="text-[10px]">
                  {rec}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-400">{'\u2014'}</span>
            )}
          </div>
        </ChangedFieldHighlight>
      )}

      {/* Key fields grid */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Channel section */}
      {channelChanged ? (
        <ChangedFieldHighlight
          label="Channel"
          fieldKey="channel"
          changes={changes}
          checklist={checklist}
        >
          {channel && (
            <ChannelDetails channel={channel} channelType={channelType} />
          )}
        </ChangedFieldHighlight>
      ) : (
        channel && (
          <div className="space-y-0.5">
            <ChannelDetails channel={channel} channelType={channelType} />
          </div>
        )
      )}

      {/* Transmission statuses (changed only) */}
      {transmissions
        .filter((t) => t.changed)
        .map((t) => (
          <ChangedFieldHighlight
            key={t.type}
            label={`${t.type === 'fax' ? 'Fax' : 'Email'} Transmission`}
            fieldKey={`${t.type}_transmission_status`}
            changes={changes}
            checklist={checklist}
          >
            <TransmissionStatusDisplay status={t.status} />
          </ChangedFieldHighlight>
        ))}

      {/* Metadata changes */}
      {metadataChanged && (
        <ChangedFieldHighlight
          label="Metadata"
          fieldKey="metadata"
          changes={changes}
          checklist={checklist}
        >
          <p className="text-xs text-gray-900">
            Automation metadata updated
          </p>
        </ChangedFieldHighlight>
      )}

      {/* Secondary fields (changed only) */}
      <KeyFieldsGrid
        fields={changedSecondaryFields}
        data={data}
        changes={changes}
        checklist={checklist}
      />

      {/* Show More Section for unchanged content */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Unchanged channel */}
          {channel && !channelChanged && (
            <div className="space-y-0.5">
              <ChannelDetails channel={channel} channelType={channelType} />
            </div>
          )}

          {/* Unchanged transmissions */}
          {!transmissionsChanged &&
            transmissions.length > 0 &&
            transmissions.map((t) => (
              <div key={t.type} className="space-y-0.5">
                <div className="flex items-center gap-1.5 px-1 py-0.5">
                  <span className="text-xs text-gray-400 shrink-0">
                    {t.type === 'fax' ? 'Fax' : 'Email'} Transmission:
                  </span>
                  <TransmissionStatusDisplay status={t.status} />
                </div>
              </div>
            ))}

          {/* Unchanged secondary fields */}
          <KeyFieldsGridUnchanged
            fields={unchangedSecondaryFields}
            data={data}
            changes={changes}
            checklist={checklist}
          />
        </ShowMoreSection>
      )}

      {/* Transfer button (hidden when nested — parent owns the button) */}
      {!nested && checklist.showButton && (
        <TransferButton
          enabled={checklist.buttonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

/** Compact display of channel contact details */
const ChannelDetails: React.FC<{
  channel: Record<string, unknown>;
  channelType?: TreatmentRecordRequestChannelType;
}> = ({ channel, channelType }) => {
  const Icon = channelType ? CHANNEL_ICONS[channelType] : Phone;
  const contactValues = [
    channel.phone_number && { label: 'Phone', value: channel.phone_number },
    channel.fax && { label: 'Fax', value: channel.fax },
    channel.email && { label: 'Email', value: channel.email },
    channel.address && { label: 'Address', value: channel.address },
  ].filter(Boolean) as { label: string; value: unknown }[];

  return (
    <div className="space-y-0.5">
      {channelType && (
        <div className="flex items-center gap-1.5 px-1 py-0.5">
          <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <span className="text-sm text-gray-700">
            {formatEnumValue(channelType)}
          </span>
        </div>
      )}
      {contactValues.map((cv) => (
        <div
          key={cv.label}
          className="flex items-baseline gap-1.5 px-1 py-0.5"
        >
          <span className="text-xs text-gray-400 shrink-0">
            {cv.label}:
          </span>
          <span className="text-sm text-gray-700 truncate">
            {String(cv.value)}
          </span>
        </div>
      ))}
      {!!channel.instructions && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">Notes:</span>
          <span className="text-sm text-gray-700">
            {String(channel.instructions)}
          </span>
        </div>
      )}
    </div>
  );
};

/** Compact transmission status display */
const TransmissionStatusDisplay: React.FC<{
  status: Record<string, unknown>;
}> = ({ status }) => {
  const txStatus = status.status as string | undefined;
  const sentAt = status.sent_at as string | undefined;
  const errorMessage = status.error_message as string | undefined;

  return (
    <div className="space-y-0.5">
      {txStatus && (
        <span className="text-sm text-gray-900">
          {formatEnumValue(txStatus)}
        </span>
      )}
      {sentAt && (
        <p className="text-xs text-gray-500">
          Sent:{' '}
          {new Date(sentAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
      )}
      {errorMessage && (
        <p className="text-xs text-red-600">{errorMessage}</p>
      )}
    </div>
  );
};
